import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, money, prettyDate } from "@/lib/api";
import { Panel } from "@/components/admin/Panel";
import { StatusPill, displayStatus } from "@/components/admin/status";

const CARDS = [["gross_bookings", "Gross bookings"], ["collected", "Collected"], ["refunds", "Refunds"], ["net", "Net"], ["outstanding", "Outstanding"], ["pending_amount", "Pending checkout"]];

const Summary = ({ s, testid }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-l border-border" data-testid={testid}>
    {CARDS.map(([k, l]) => <div key={k} className="p-4 border-r border-b border-border"><p className="font-serif text-2xl" data-testid={`${testid}-${k}`}>{money(s[k])}</p><p className="text-xs text-muted-foreground">{l}</p></div>)}
  </div>
);

export const PaymentsRevenue = () => {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ from: "", to: "", status: "all", pay: "all" });
  useEffect(() => { api.get("/admin/payments").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const rows = d.rows.filter((b) => (!f.from || b.created_at.slice(0, 10) >= f.from) && (!f.to || b.created_at.slice(0, 10) <= f.to)
    && (f.status === "all" || displayStatus(b) === f.status) && (f.pay === "all" || b.payment_status === f.pay));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <Panel title="Payments & Revenue" desc="Built only from Stripe-verified payment records. Bookings are charged in full at checkout, so there are no outstanding balances." testid="payments-panel">
        <p className="text-sm font-semibold mb-2">Real revenue (live payments)</p>
        <Summary s={d.real} testid="real-summary" />
        {d.test.paid_count + d.test.pending_count > 0 && (<><p className="text-sm font-semibold mt-6 mb-2">Test-mode transactions (not real revenue)</p><Summary s={d.test} testid="test-summary" /></>)}
        <p className="text-xs text-muted-foreground mt-2">Failed payment attempts: {d.real.failed_attempts + d.test.failed_attempts}</p>
        <div className="flex flex-wrap gap-3 mt-8 mb-4 items-end">
          <label className="text-xs">Booked from<Input type="date" value={f.from} onChange={set("from")} data-testid="pay-from-date" /></label>
          <label className="text-xs">to<Input type="date" value={f.to} onChange={set("to")} data-testid="pay-to-date" /></label>
          <select value={f.status} onChange={set("status")} className="border border-input rounded-md h-10 px-3 text-sm bg-white" data-testid="pay-status-filter">{["all", "pending", "confirmed", "completed", "cancelled", "expired"].map((s) => <option key={s}>{s}</option>)}</select>
          <select value={f.pay} onChange={set("pay")} className="border border-input rounded-md h-10 px-3 text-sm bg-white" data-testid="pay-payment-filter">{["all", "pending", "paid", "partially_refunded", "refunded", "expired", "cancelled"].map((s) => <option key={s}>{s}</option>)}</select>
        </div>
        {!rows.length ? <p className="text-sm text-muted-foreground" data-testid="payments-empty">No payment records yet.</p> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{["Ref", "Guest", "Stay", "Accommodation", "Fees & taxes", "Total", "Paid", "Refunded", "Balance", "Status", "Stripe"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {rows.map((b) => {
                  const acc = b.quote.lines[0]?.amount || 0;
                  return (
                    <TableRow key={b.id} data-testid={`payment-row-${b.ref}`}>
                      <TableCell className="font-medium">{b.ref}{b.test_mode && <span className="block text-[10px] uppercase text-[hsl(30_60%_35%)]">Test</span>}</TableCell>
                      <TableCell className="text-sm">{b.name}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{prettyDate(b.check_in)}<br />→ {prettyDate(b.check_out)} · {b.nights}n</TableCell>
                      <TableCell>{money(acc)}</TableCell><TableCell>{money(b.total - acc)}</TableCell><TableCell>{money(b.total)}</TableCell>
                      <TableCell>{money(b.amount_paid)}</TableCell><TableCell>{money(b.amount_refunded)}</TableCell><TableCell>{money(0)}</TableCell>
                      <TableCell><StatusPill value={b.payment_status} /></TableCell>
                      <TableCell>{b.payment_intent ? <a href={`${d.dashboard_base}${b.payment_intent}`} target="_blank" rel="noreferrer" className="text-xs underline flex items-center gap-1" data-testid={`stripe-link-${b.ref}`}>{b.payment_intent.slice(-8)} <ExternalLink className="w-3 h-3" /></a> : <span className="text-xs text-muted-foreground">None</span>}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
    </>
  );
};
